import React, { useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
// _mock
import { _pricingPlans } from '../../_mock';
//
import PricingCard from './pricing-card';
import Main from 'layouts/Main';

// ----------------------------------------------------------------------

export default function PricingNewView() {
  const [billingPeriod, setBillingPeriod] = useState('6month');

  const billingOptions = [
    { value: '3month', label: '3 MONTHS', discount: null },
    { value: '6month', label: '6 MONTHS', discount: 'Save up to 20%' },
    { value: '1year', label: '1 YEAR', discount: 'Save up to 30%' },
  ];

  return (
    <Main bgcolor={'background.paper'}>
      <Container
        sx={{
          pt: 8,
          pb: 10,
          minHeight: 1,
        }}
      >
      <Typography variant="h2" align="center" sx={{ mb: 2 }}>
        Flexible plans for enterprises of all sizes and needs
      </Typography>

      <Typography align="center" sx={{ color: 'text.secondary' }}>
        Improve your hardware design efficiency with Partix: We handle the extras, you focus on engineering excellence.
      </Typography>

      <Box sx={{ mt: 9, mb: 5, position: 'relative' }}>
        <Stack direction="column" alignItems="center" spacing={2}>
          <ButtonGroup variant="outlined" aria-label="billing period" sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 167, 111, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 167, 111, 0.1)',
          }}>
            {billingOptions.map((option) => (
              <Button
                key={option.value}
                variant={billingPeriod === option.value ? 'contained' : 'outlined'}
                onClick={() => setBillingPeriod(option.value)}
                sx={{
                  px: 5,
                  py: 2.5,
                  borderRadius: 0,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  position: 'relative',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: billingPeriod === option.value
                    ? 'linear-gradient(135deg, #00A76F 0%, #00C853 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  color: billingPeriod === option.value ? 'white' : 'text.primary',
                  border: 'none',
                  boxShadow: billingPeriod === option.value
                    ? '0 8px 25px rgba(0, 167, 111, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: billingPeriod === option.value
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 50%, rgba(0, 0, 0, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(0, 167, 111, 0.02) 0%, rgba(0, 167, 111, 0.05) 100%)',
                    opacity: billingPeriod === option.value ? 1 : 0.5,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    bgcolor: billingPeriod === option.value
                      ? 'linear-gradient(135deg, #00C853 0%, #00A76F 100%)'
                      : 'rgba(0, 167, 111, 0.08)',
                    boxShadow: billingPeriod === option.value
                      ? '0 12px 35px rgba(0, 167, 111, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      : '0 4px 15px rgba(0, 167, 111, 0.15)',
                    transform: billingPeriod === option.value ? 'translateY(-2px)' : 'translateY(-1px)',
                    '&::before': {
                      opacity: 1,
                    },
                  },
                  '&:not(:last-child)': {
                    borderRight: '1px solid rgba(0, 167, 111, 0.1)',
                  },
                }}
              >
                <Stack direction="column" alignItems="center" spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
                  {option.discount && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: billingPeriod === option.value ? 'rgba(255, 255, 255, 0.9)' : '#00A76F',
                        textShadow: billingPeriod === option.value ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                      }}
                    >
                      {option.discount}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      color: 'inherit',
                      textShadow: billingPeriod === option.value ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                    }}
                  >
                    {option.label}
                  </Typography>
                </Stack>
              </Button>
            ))}
          </ButtonGroup>
        </Stack>
      </Box>

      <Box
        gap={{ xs: 3, md: 0 }}
        display="grid"
        alignItems={{ md: 'center' }}
        gridTemplateColumns={{ md: 'repeat(3, 1fr)' }}
      >
        {_pricingPlans.map((card, index) => (
          <PricingCard key={card.subscription} card={card} index={index} billingPeriod={billingPeriod} />
        ))}
      </Box>
      </Container>
    </Main>
  );
}
